import { BENEFIT, type Benefit } from '~/app/auth/enums/TenantSubscriptionPlan';
import { FEATURE_FLAG } from '~/app/enums/FeatureFlag';

export interface ModuleInterface {
    key: string;
    defaultPath: () => string;
    icon: string;
    color: string;
    sidebar?: string;
    current: boolean;
    checkPermission: boolean;
    permissionKey?: string;
    children?: ChildrenItem[];
    requiredBenefit?: Benefit;
    basePath?: string;
    // Children used by the route-upsell guard / middleware to protect sub-pages, but NOT
    // rendered as a sidebar submenu (Visibility exposes its sub-pages as in-page tabs).
    hideSubmenu?: boolean;
}

export type ChildrenItem = {
    permission?: string;
    role?: string;
    to: string;
    icon?: string;
    label: string;
    extraActivePath?: string;
    key?: string;
    requiredBenefit?: Benefit;
};

export default function useModules() {
    // const { authed } = useAuthStore();
    // Facturation always opens on Devis (quotes), even when the plan gates it — the upsell
    // flow handles the restricted case, so the entry point stays consistent across plans.
    const invoicingDefaultPath = (): string => 'quotes';
    const modules = useState('modules', () => [
        {
            key: 'dashboard',
            defaultPath: (): string => '',
            icon: 'chart-bar',
            color: '#32A3FF',
            sidebar: 'apps',
            current: true,
            checkPermission: true,
            permissionKey: 'app:dashboard',
        },
        {
            key: 'crm',
            defaultPath: (): string => 'leads',
            icon: 'inbox',
            color: '#00b894',
            sidebar: 'apps',
            current: false,
            checkPermission: true,
            permissionKey: 'crm:lead:view_any',
            requiredBenefit: BENEFIT.CRM,
        },
        {
            // Marketplace Messages (Phase 5). Plan/role gating wired in Phase 10 (TDD-3522)
            // via the menu-visibility key messages:access. Unread badge is wired in 5.2.
            key: 'messages',
            defaultPath: () => '',
            // Envelope, per the sidebar design (rendered as `fa-jelly fa-envelope`). TDM-590.
            icon: 'envelope',
            color: '#6366F1',
            sidebar: 'apps',
            current: false,
            checkPermission: true,
            permissionKey: 'messages:access',
        },
        {
            key: 'contacts',
            defaultPath: () => '',
            icon: 'users',
            color: '#00b894',
            sidebar: 'apps',
            permissionKey: 'contact:contact:view_any',
            current: false,
            checkPermission: true,
        },
        {
            key: 'invoicing',
            defaultPath: invoicingDefaultPath,
            icon: 'file',
            color: '#3498db',
            sidebar: 'apps',
            current: false,
            checkPermission: true,
            children: [
                {
                    permission: 'invoicing:quote:view_any',
                    to: '/invoicing/quotes',
                    icon: 'fa-file-circle-check',
                    label: 'invoicing.sidebar.quotes',
                },
                {
                    permission: 'invoicing:invoice:view_any',
                    to: '/invoicing/invoices',
                    icon: 'fa-file-circle-plus',
                    label: 'invoicing.sidebar.invoices',
                },
                {
                    permission: 'invoicing:credit-note:view_any',
                    to: '/invoicing/credit-notes',
                    icon: 'fa-file-circle-minus',
                    label: 'invoicing.sidebar.credit-notes',
                },
                {
                    permission: 'invoicing:order-slip:view_any',
                    to: '/invoicing/order-slips',
                    icon: 'fa-cart-shopping',
                    label: 'invoicing.sidebar.order-slips',
                },
                {
                    permission: 'invoicing:delivery-slip:view_any',
                    to: '/invoicing/delivery-slips',
                    icon: 'fa-truck',
                    label: 'invoicing.sidebar.delivery-slips',
                },
                {
                    permission: 'inventory:product:view_any',
                    to: '/invoicing/inventory',
                    icon: 'fa-book',
                    label: 'invoicing.sidebar.inventory',
                },
                {
                    role: 'super-admin',
                    to: '/bankaccount/bank-accounts',
                    icon: 'fa-credit-card',
                    label: 'bankaccount.sidebar.bank-accounts',
                    permission: 'app:bank_connection',
                },
                // {
                //     to: '/invoicing/export',
                //     icon: 'fa-file-export',
                //     label: 'settings.sidebar.export',
                // },
                // {
                //     to: '/invoicing/import',
                //     icon: 'fa-file-import',
                //     label: 'settings.sidebar.import',
                // },
                {
                    role: 'super-admin',
                    to: '/invoicing/settings',
                    icon: 'fa-gear',
                    label: 'invoicing.sidebar.settings',
                },
            ],
        },
        {
            key: 'expenses',
            defaultPath: () => '',
            icon: 'ticket',
            color: '#3498db',
            sidebar: 'apps',
            basePath: '/invoicing/expenses',
            current: false,
            checkPermission: true,
            permissionKey: 'invoicing:expense:view_any',
        },
        {
            key: 'organization',
            defaultPath: (): string => 'calendar',
            icon: 'calendar',
            color: '#8518C8',
            sidebar: 'apps',
            current: false,
            checkPermission: true,
            permissionKey: 'planning:access',
            children: [
                {
                    permission: 'planning:calendar_event:view_any',
                    to: '/organization/calendar',
                    label: 'modules.calendar',
                    extraActivePath: '/organization/calendar/',
                },
                {
                    permission: 'planning:planning_item:view_any',
                    to: '/organization/planning',
                    label: 'modules.planning',
                },
                {
                    permission: 'planning:time_activity:view_any',
                    to: '/organization/timesheet',
                    label: 'modules.timesheet',
                    requiredBenefit: BENEFIT.TIME_SHEETS,
                },
            ],
        },
        {
            key: 'task',
            defaultPath: () => '',
            icon: 'circle-check',
            color: '#8518D9',
            sidebar: 'apps',
            current: false,
            checkPermission: true,
            permissionKey: 'task:task:view_any',
        },
        {
            key: 'worksite',
            defaultPath: (): string => 'worksites',
            icon: 'folders',
            color: '#10A4D3',
            sidebar: 'apps',
            current: false,
            checkPermission: true,
            permissionKey: 'worksite:worksite:view_any',

            children: [
                {
                    permission: 'worksite:worksite:view_any',
                    to: '/worksite/worksites',
                    icon: 'fa-helmet-safety',
                    label: 'worksite.sidebar.worksites',
                    extraActivePath: '/worksite/worksite/',
                },
                {
                    permission: 'planning:planning_item:view_any',
                    to: '/worksite/planning',
                    icon: 'fa-bars-progress',
                    label: 'worksite.sidebar.planning',
                    requiredBenefit: BENEFIT.PLANNING,
                },
                {
                    permission: 'worksite:worksite_profitability_document:view',
                    to: '/worksite/profitability',
                    icon: 'fa-chart-line-up',
                    label: 'worksite.sidebar.profitability',
                    requiredBenefit: BENEFIT.PROFITABILITY,
                },
                {
                    permission: 'worksite:worksite_progress_billing:view_any',
                    to: '/worksite/worksite-progress-billings',
                    icon: 'fa-arrow-progress',
                    label: 'worksite.sidebar.progress_billings',
                    requiredBenefit: BENEFIT.PROGRESS_BILLINGS,
                },
                {
                    permission: 'worksite:closure_report:view_any',
                    to: '/worksite/worksite-closure-reports',
                    icon: 'fa-flag-checkered',
                    label: 'worksite.sidebar.closure_report',
                    requiredBenefit: BENEFIT.WORKSITE_CLOSURE_REPORTS,
                },
            ],
        },
        {
            key: 'visibility',
            defaultPath: () => '',
            icon: 'desktop',
            color: '#32A3FF',
            sidebar: 'apps',
            current: false,
            checkPermission: true,
            permissionKey: 'marketplace:access',
            // Sub-pages are gated per-tab; declared as children so the route-upsell guard
            // protects them (middleware block + inline upsell on deep-link). hideSubmenu keeps
            // them out of the sidebar — the page renders them as tabs.
            hideSubmenu: true,
            children: [
                { permission: 'marketplace:page', to: '/visibility/my-page', label: 'visibility.tabs.my_page' },
                { permission: 'marketplace:reviews', to: '/visibility/reviews', label: 'visibility.tabs.reviews' },
                { permission: 'marketplace:portfolio', to: '/visibility/photos', label: 'visibility.tabs.photos' },
                // My trades (Profile review): a plan without it gets the same inline upsell as the other tabs.
                { permission: 'marketplace:workfields', to: '/visibility/sectors', label: 'profile_review.tab' },
            ],
        },
        {
            key: 'settings',
            icon: 'gear',
            color: '#EA7133',
            defaultPath: (): string => 'company',
            sidebar: 'extra',
            current: false,
            checkPermission: true,
            children: [
                {
                    to: '/settings/company',
                    icon: 'fa-helmet-safety',
                    label: 'settings.sidebar.company',
                },
                {
                    to: '/settings/profile',
                    icon: 'fa-user',
                    label: 'settings.sidebar.profile',
                },
                {
                    to: '/settings/users',
                    icon: 'fa-users',
                    label: 'settings.sidebar.users',
                },
                {
                    to: '/settings/subscription',
                    icon: 'fa-credit-card',
                    label: 'settings.sidebar.subscription',
                    key: 'subscription',
                },
                {
                    to: '/settings/invoices',
                    icon: 'fa-file-invoice',
                    label: 'settings.sidebar.invoices',
                    permission: 'app:buy_credits',
                },
                {
                    to: '/settings/credit',
                    icon: 'fa-file-invoice',
                    label: 'settings.sidebar.credit',
                    permission: 'app:buy_credits',
                },
                {
                    to: '/settings/outbox',
                    icon: 'fa-inbox-out',
                    label: 'settings.sidebar.outbox',
                },
                {
                    to: '/settings/terms-of-service',
                    icon: 'fa-file-contract',
                    label: 'settings.sidebar.terms-of-service',
                },
            ],
        },
    ]);

    const currentModule = computed(() => {
        return modules.value.find((module) => module.current);
    });

    const unsetCurrentModule = () => {
        modules.value.forEach((module) => {
            module.current = false;
        });
    };

    const setCurrentModule = (key: string, redirect = true) => {
        modules.value.forEach((module) => {
            module.current = module.key === key;
        });

        if (redirect && currentModule.value) {
            const path = currentModule.value.basePath ?? `/${currentModule.value.key}/${currentModule.value.defaultPath()}`;
            return navigateTo(path);
        }
    };

    const featureFlag = useFeatureFlag();
    // Messages & Visibility (TDD-3649) stay hidden from the sidebar until the `messages_visibility`
    // flag is on, so the SECL work can ship to develop/prod dark. The flag hides the entry, it does
    // not redirect. A ref refreshed via onFlagsResolved (not a computed) because PostHog flags are
    // bootstrapped to false and resolve async — posthog.isFeatureEnabled is not a reactive source,
    // so a computed would cache the bootstrap value forever.
    const isMessagesVisibilityEnabled = ref(featureFlag.isEnabled(FEATURE_FLAG.MESSAGES_VISIBILITY));
    featureFlag.onFlagsResolved(() => {
        isMessagesVisibilityEnabled.value = featureFlag.isEnabled(FEATURE_FLAG.MESSAGES_VISIBILITY);
    });
    const isModuleFlagged = (key: string): boolean => (key === 'messages' || key === 'visibility') && !isMessagesVisibilityEnabled.value;

    const appsModules = computed(() => {
        return modules.value.filter((module) => module.sidebar === 'apps' && !isModuleFlagged(module.key));
    });
    const extraModules = computed(() => {
        return modules.value.filter((module) => module.sidebar === 'extra');
    });

    return {
        modules,
        appsModules,
        extraModules,
        currentModule,
        setCurrentModule,
        unsetCurrentModule,
    };
}
